import React, { useEffect, useState } from 'react';
import { Typography, Button } from '@mui/material';
import { Navigate, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useOrganization } from '../hooks/useOrganization';
import TeamInvite from '../components/Layout/TeamInvite';
import CreateEmpGrp from '../components/Layout/CreateEmpGrp';
import OrganizationCard from '../components/Team/OrganizationCard';
import Invite from '../components/Team/Invite';
import { InputTextField } from '../components/Input/InputTextField';
import { InputSelect } from '../components/Input/InputSelect';
import { MailPlus, Building2, Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-hot-toast';
import { successToastConfig, dangerToastConfig } from '../utils/data';
import EditOrgCard from "../components/Layout/EditOrgCard";

const INVITES_PER_PAGE = 9;

const Organizations = () => {
  const [isEditOrg, setIsEditOrg] = useState(false);
  const [showCreateEmpModal, setShowCreateEmpModal] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [activeView, setActiveView] = useState('organizations');
  const [invitePage, setInvitePage] = useState(1);
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteStatus, setInviteStatus] = useState('all');
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { organizations, organizationInvites, fetchOrganizations, fetchOrganizationInvites, deleteOrganization, accountSelected } = useOrganization();

  useEffect(() => {
      fetchOrganizations();
      fetchOrganizationInvites();

  }, [accountSelected]);

  useEffect(() => {
    setInvitePage(1);
  }, [organizationInvites, inviteSearch, inviteStatus]);

  // Redirect if user is not admin
  if (!user?.roles?.includes('admin') && !user?.roles?.includes('superadmin')) {
    return <Navigate to="/" replace />;
  }

  const handleInviteClick = () => {
    // Only navigate if not already on the invite page
    if (location.pathname !== '/organizations/invite') {
      navigate('/organizations/invite');
    }
  };

  const handleCreateEmpGroupClick = () => {
    setShowCreateEmpModal(true);
  }

  const onDeleteOrg = async (id) => {
    const result = await deleteOrganization(id);
    if(result.status === 200){
      toast('Organization deleted successfully', successToastConfig);
      fetchOrganizations();
    } else {
      toast('Something went wrong. Please try again', dangerToastConfig);
    }
  };

  const handleOrgEdit = (org) => {
    setIsEditOrg(true);
    setEditOrg(org);
  };

  const handleOrgEditClose = () => {
    setIsEditOrg(false);
    setEditOrg(null);
  };

  const handleEmpGrpAddClose = () => {
    setShowCreateEmpModal(false)
    fetchOrganizations();
  };

  const filteredInvites = organizationInvites.filter((invite) => {
    const normalizedSearch = inviteSearch.trim().toLowerCase();
    const inviteStatusValue = invite.acceptedAt === null ? 'pending' : 'accepted';

    const matchesSearch = normalizedSearch === '' || [
      invite.email,
      invite.role,
      invite.code,
    ].filter(Boolean).some((value) => value.toLowerCase().includes(normalizedSearch));

    const matchesStatus = inviteStatus === 'all' || inviteStatusValue === inviteStatus;

    return matchesSearch && matchesStatus;
  });

  const totalInvitePages = Math.max(1, Math.ceil(filteredInvites.length / INVITES_PER_PAGE));
  const paginatedInvites = filteredInvites.slice(
    (invitePage - 1) * INVITES_PER_PAGE,
    invitePage * INVITES_PER_PAGE
  );

  return (
    <>
      {isEditOrg && <EditOrgCard org={editOrg} onClose={handleOrgEditClose} />}
      {showCreateEmpModal && <CreateEmpGrp invite="organization" onClose={handleEmpGrpAddClose} />}
      <div className="main-content page-team">
        <Routes>
          <Route path="/invite" element={<TeamInvite invite="organization"/>} />
        </Routes>
        <div
          style={{
            background: 'var(--bg-panel)',
            border: '1px solid var(--border-color)',
            borderRadius: 18,
            padding: 24,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {accountSelected && activeView === 'organizations' && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateEmpGroupClick}
                >
                  <Building2 className="nav-icon" style={{ marginRight: 11 }} size={16} /> Create Employer Group
                </Button>
              )}
              {accountSelected && activeView === 'invites' && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleInviteClick}
                >
                  <MailPlus className="nav-icon" style={{ marginRight: 11 }} size={16} /> Invite analyst
                </Button>
              )}
            </div>
          </div>

          <div className="bubble-tabs">
            <Button
              variant="text"
              onClick={() => setActiveView('organizations')}
              className={`bubble-tab${activeView === 'organizations' ? ' bubble-tab--active' : ''}`}
            >
              Employer Groups
            </Button>
            <Button
              variant="text"
              onClick={() => setActiveView('invites')}
              className={`bubble-tab${activeView === 'invites' ? ' bubble-tab--active' : ''}`}
            >
              Invites
            </Button>
          </div>

          {activeView === 'organizations' ? (
            <div>
              <div className="flex-row align-center" style={{ marginBottom: 16 }}>
                <Building2 size={16} style={{ color: 'var(--icon-color-secondary)' }} />
                <h4 style={{ margin: 0, marginLeft: 10 }}>
                  Employer Groups
                </h4>
              </div>

              <div
                style={{
                  maxHeight: 520,
                  overflowY: 'auto',
                  paddingRight: 8,
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                    gap: 14,
                  }}
                >
                  {organizations.map((organization) => (
                    <OrganizationCard 
                      key={organization._id} 
                      organization={organization}
                      onDelete={(id) => onDeleteOrg(id)}
                      onEdit={(organization) => handleOrgEdit(organization)}
                    />
                  ))}
                </div>
              </div>

              {organizations.length === 0 && (
                <Typography variant="body1" color="textSecondary" style={{ marginTop: 20 }}>
                  No employer groups found.
                </Typography>
              )}
            </div>
          ) : (
            <div>
              <div className="flex-row align-center" style={{ marginBottom: 16 }}>
                <Mail size={16} style={{ color: 'var(--icon-color-secondary)' }} />
                <h4 style={{ margin: 0, marginLeft: 10 }}>
                  Invites
                </h4>
              </div>

              <div style={{ marginTop: 10 }}>
                <div style={{ maxWidth: 1320, display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 16 }}>
                  <div style={{ flex: 1, maxWidth: 360 }}>
                    <InputTextField
                      placeholder='Search invites'
                      value={inviteSearch}
                      onChange={setInviteSearch}
                    />
                  </div>
                  <div style={{ width: 220 }}>
                    <InputSelect
                      title="Status"
                      value={inviteStatus}
                      onChange={setInviteStatus}
                      options={['All', 'Pending', 'Accepted']}
                      optionsValues={['all', 'pending', 'accepted']}
                    />
                  </div>
                </div>

                <div style={{ maxWidth: 1320 }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(280px, 1fr))',
                      gap: 16,
                    }}
                  >
                  {paginatedInvites.map((invite) => (
                    <div key={invite._id}>
                      <Invite
                        invite={invite}
                      />
                    </div>
                  ))}
                  </div>
                </div>

                {organizationInvites.length === 0 && (
                  <Typography variant="body1" color="textSecondary">
                    No pending invites.
                  </Typography>
                )}
                {organizationInvites.length > 0 && filteredInvites.length === 0 && (
                  <Typography variant="body1" color="textSecondary">
                    No invites match the current filters.
                  </Typography>
                )}
                {filteredInvites.length > INVITES_PER_PAGE && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 20, maxWidth: 1320 }}>
                    <Button
                      variant="outlined"
                      onClick={() => setInvitePage((currentPage) => Math.max(1, currentPage - 1))}
                      disabled={invitePage === 1}
                    >
                      <ChevronLeft size={16} style={{ marginRight: 6 }} />
                      Prev
                    </Button>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      Page {invitePage} of {totalInvitePages}
                    </span>
                    <Button
                      variant="outlined"
                      onClick={() => setInvitePage((currentPage) => Math.min(totalInvitePages, currentPage + 1))}
                      disabled={invitePage === totalInvitePages}
                    >
                      Next
                      <ChevronRight size={16} style={{ marginLeft: 6 }} />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Organizations; 
